import React from "react";

import SectionHeading from "components/SectionHeading";

import sharedClasses from "../style.module.scss";
import classes from "../docTables.module.scss";

const PROGRAMS = [
    {
        program: "NBC 2015",
        useWhen: "Part 9 energy-performance compliance under NBC 9.36.5.",
        package: "None",
    },
    {
        program: "NBC 2020",
        useWhen: "Part 9 energy-performance compliance under NBC 9.36.5.",
        package: "None",
    },
    {
        program: "NBC 2025",
        useWhen: "Part 9 energy-performance compliance under NBC 9.36.6.",
        package: "None",
    },
    {
        program: "Ontario SB-12",
        useWhen: "Ontario compliance under OBC Supplementary Standard SB-12.",
        package: "Required",
    },
    {
        program: "NECB (coming soon)",
        useWhen: "Part 3 buildings.",
        package: "None",
    },
];

const ALTERATIONS = [
    [
        "House envelope",
        "Volumes, floor areas, and the wall, ceiling, and foundation components stay. Their RSI values follow the code or package.",
    ],
    [
        "Windows",
        "Rebuilt as equal windows facing north, east, south, and west, sized to the code fenestration area. On a whole-house model they are placed on the largest wall. On a room-by-room* model they stay on each wall. U-value and solar heat gain coefficient follow the code or package.",
    ],
    [
        "Doors",
        "Stay on the same walls. NBC resizes them to the reference-house door area. Ontario keeps the proposed size. Opaque RSI follows the code or package.",
    ],
    [
        "Basements",
        "A basement wall more than 0.6 m above grade gets a pony wall when the proposed house does not already have one. An existing pony wall is resized to the above-grade height. An Ontario wall that is already more than half above grade keeps the foundation wall and uses the above-grade wall RSI instead of adding a pony wall.",
    ],
    [
        "Weather location",
        "The proposed weather location is kept. User HDD overrides are used if provided in the Program section of the file.",
    ],
    ["Dwelling unit count", "Multi-unit buildings keep their unit count."],
    ["Heating and cooling", "System type, fuel, and efficiency follow the climate bin and the code rules."],
    [
        "Temperatures",
        "Setpoints and setback behaviour follow the selected code. Ontario keeps standard operating conditions.",
    ],
    ["Ventilation", "Flow, heat recovery, schedule, and fan power can change."],
    [
        "Domestic hot water",
        "Equipment, efficiency, and draw convention follow the selected code or package. Hot water draws follow HOT2000 overrides.",
    ],
    ["On-site renewables", "On-site generation is left out of the reference house."],
];

const OPERATING_INPUTS = [
    {
        input: "Living space temperature",
        nbc2015: "21 °C",
        nbc2020: "20 °C",
        nbc2025: "20 °C",
        ontario: "Standard operating conditions",
    },
    {
        input: "Basement",
        nbc2015: "19 °C, heated",
        nbc2020: "19 °C, heated",
        nbc2025: "19 °C, heated",
        ontario: "Standard operating conditions",
    },
    {
        input: "Conditioned crawl space",
        nbc2015: "15 °C",
        nbc2020: "15 °C",
        nbc2025: "15 °C",
        ontario: "Standard operating conditions",
    },
    {
        input: "Cooling",
        nbc2015: "25 °C",
        nbc2020: "25 °C",
        nbc2025: "25 °C",
        ontario: "Standard operating conditions",
    },
    {
        input: "Nighttime setback",
        nbc2015: "Removed",
        nbc2020: "Removed",
        nbc2025: "Removed",
        ontario: "Standard operating conditions",
    },
    {
        input: "Domestic hot water",
        nbc2015: "Code total recorded: 225 L/day for a house, 140 L/day per dwelling unit",
        nbc2020: "Code total recorded: 211 L/day for a house, 141 L/day per dwelling unit",
        nbc2025: "Code total recorded: 211 L/day for a house, 141 L/day per dwelling unit",
        ontario: "EnerGuide Rule Sets Table 3 fixture schedule",
    },
    {
        input: "Electrical base loads",
        nbc2015:
            "Code total recorded: 22.4 kWh/day for a house. Occupant count stays at standard operating conditions.",
        nbc2020: "Standard operating conditions",
        nbc2025: "Standard operating conditions",
        ontario: "Standard operating conditions",
    },
    {
        input: "Airtightness",
        nbc2015: "2.5 ACH50 for all buildings.",
        nbc2020: "2.5 ACH50 detached. 3.0 ACH50 attached, unguarded.",
        nbc2025: "2.5 ACH50 detached. 3.0 ACH50 attached, unguarded.",
        ontario: "3.0 ACH50 detached. 3.5 ACH50 attached.",
    },
    {
        input: "Fenestration SHGC",
        nbc2015: "0.26",
        nbc2020: "0.26",
        nbc2025: "0.26",
        ontario: "0.26",
    },
    {
        input: "Fenestration area",
        nbc2015: "Brought into the 17–22% band when the proposed house is outside that range.",
        nbc2020: "Brought into the 17–22% band when the proposed house is outside that range.",
        nbc2025: "Brought into the 17–22% band when the proposed house is outside that range.",
        ontario:
            "Below 17%, area and U-value stay. From 17–22%, area stays and window U-value improves one step. Above 22%, area is capped at 22% and U-value improves one step. Doors keep the proposed size at RSI 0.7.",
    },
    {
        input: "On-site renewables",
        nbc2015: "Excluded",
        nbc2020: "Excluded",
        nbc2025: "Excluded",
        ontario: "Excluded",
    },
    {
        input: "Heat-pump space heating",
        nbc2015: "Heating-only heat pump removed. Heating and cooling heat pump becomes air conditioning.",
        nbc2020: "Heating-only heat pump removed. Heating and cooling heat pump becomes air conditioning.",
        nbc2025: "Heating-only heat pump removed. Heating and cooling heat pump becomes air conditioning.",
        ontario: "Heating-only heat pump removed. Heating and cooling heat pump becomes air conditioning at SEER 13.",
    },
];

export default () => (
    <div>
        <SectionHeading
            title="Reference Houses"
            subtitle="Code reference houses Volta SNAP builds from the proposed house."
        />
        <div className={sharedClasses.contentWrapper}>
            <section className={classes.section}>
                <h3>About</h3>
                <p>
                    A reference house is the code benchmark built from the proposed house. Many forms of code compliance
                    compare the proposed house with that reference house benchmark.
                </p>
                <p>Volta SNAP builds the reference house in two steps:</p>
                <ol>
                    <li>
                        Apply standard operating conditions (SOC) to the proposed house. An SOC baseline is applied to
                        the transformation regardless of whether the user has requested SOC approximation for their
                        other simulations. See <a href="/documentation/operating-conditions">SOC Approximation</a>.
                    </li>
                    <li>Apply the prescriptive reference house inputs for the code you selected.</li>
                </ol>
                <p>
                    The reference house transformation can be applied in the Upgrades table as a package, or the model
                    can be replaced with the reference house.
                </p>
                <p>
                    All of the contents of the reference house can be viewed, and it can be exported. We recommend
                    reviewing the reference house before using it in your work.
                </p>
                <p>If you find any issues with the reference houses, please contact support so we can address them.</p>
            </section>

            <section className={classes.section}>
                <h3>Alterations</h3>
                <p>The table below describes how Volta SNAP alters the proposed house to build the reference house.</p>
                <div className={classes.tableWrapper}>
                    <table className={`${classes.factorTable} ${classes.wrap}`}>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>How it is handled</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ALTERATIONS.map(([topic, detail]) => (
                                <tr key={topic}>
                                    <td>{topic}</td>
                                    <td>{detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className={classes.referencesNote}>
                    * A house is determined to be in room-by-room mode when the enclosure view is set to "Room", or when
                    a basement, crawlspace, slab, wall, ceiling, or exposed floor is assigned to a room other than the
                    default (unassigned) room. A room that is only listed, with nothing assigned to it, does not count,
                    including rooms that arrive on an HOT2000 import. Switching back to "Storey" view does not change
                    this while those assignments remain.
                </p>
            </section>

            <section className={classes.section}>
                <h3>Supported Reference Houses</h3>
                <div className={classes.tableWrapper}>
                    <table className={`${classes.factorTable} ${classes.wrap}`}>
                        <thead>
                            <tr>
                                <th>Reference house</th>
                                <th>Use when</th>
                                <th>Package</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PROGRAMS.map((row) => (
                                <tr key={row.program}>
                                    <td>{row.program}</td>
                                    <td>{row.useWhen}</td>
                                    <td>{row.package}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p>
                    Ontario SB-12 follows the model&apos;s heating degree days. Under 5,000 HDD is zone 1. 5,000 HDD or
                    more is zone 2. If heating degree days are missing, Volta SNAP uses 3,500, which is zone 1.
                </p>
                <div className={classes.tableWrapper}>
                    <table className={classes.factorTable}>
                        <thead>
                            <tr>
                                <th>Ontario climate zone</th>
                                <th>Heating degree days</th>
                                <th>Packages</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Zone 1</td>
                                <td>Under 5,000</td>
                                <td>A1–A6, B1–B6, C1–C4</td>
                            </tr>
                            <tr>
                                <td>Zone 2</td>
                                <td>5,000 or more</td>
                                <td>A1–A6, B1–B6, C1–C2</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className={classes.referencesNote}>
                    You choose the SB-12 package. Volta SNAP applies that column. Package A, B, and C values come from
                    OBC SB-12 Chapter 3. Modelling gaps are filled from EnerGuide Rating System Rule Sets.
                </p>
            </section>

            <section className={classes.section}>
                <h3>How to Apply a Reference House</h3>
                <h4>As a Package in the Upgrades Table</h4>
                <ol>
                    <li>Start from a complete proposed house.</li>
                    <li>
                        Click the "Add New Package" button in the Upgrades table, select your option from the "Reference
                        House Packages" drop-down menu, and click "Add".
                    </li>
                    <li>
                        The reference house column will be populated with the values for the reference house
                        (read-only).
                    </li>
                    <li>Run the simulation.</li>
                    <li>Compare the reference-house column with the proposed house.</li>
                </ol>
                <h4>Overwriting your Model (Owner Only)</h4>
                <ol>
                    <li>Start from a complete proposed house.</li>
                    <li>(Optional) Duplicate the model if you need to keep the proposed house.</li>
                    <li>
                        Click the "Replace Model" button in the Model Settings panel, select your option from the
                        "Reference House Packages" drop-down menu, and click "Replace".
                    </li>
                    <li>
                        The reference house will be applied to the model. All upgrade packages and results will be
                        removed.
                    </li>
                    <li>Run the simulation or export the file.</li>
                </ol>
            </section>

            <section className={classes.section}>
                <h3>Operating inputs</h3>
                <p>
                    These are the operating values Volta SNAP writes. Opaque RSI, fenestration U-values, and listed
                    equipment efficiencies still come from the code or SB-12 package table for the climate.
                </p>
                <div className={classes.tableWrapper}>
                    <table className={`${classes.factorTable} ${classes.wrap}`}>
                        <thead>
                            <tr>
                                <th>Input</th>
                                <th>NBC 2015</th>
                                <th>NBC 2020</th>
                                <th>NBC 2025</th>
                                <th>Ontario SB-12</th>
                            </tr>
                        </thead>
                        <tbody>
                            {OPERATING_INPUTS.map((row) => (
                                <tr key={row.input}>
                                    <td>{row.input}</td>
                                    <td>{row.nbc2015}</td>
                                    <td>{row.nbc2020}</td>
                                    <td>{row.nbc2025}</td>
                                    <td>{row.ontario}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className={classes.referencesNote}>
                    NBC occupant counts stay at the standard operating condition defaults (a house is modelled as 2
                    adults and 1 child; a multi-unit dwelling uses 2 adults per unit). HOT2000 adds occupant heat on top
                    of the electrical total, so internal gains can sit a little above a strict reading of NBC Table
                    9.36.5.4. NBC 2020 and NBC 2025 leave electrical inputs at standard operating conditions because
                    HOT2000 cannot take that table&apos;s hourly profile. Domestic hot water is still the daily total
                    from the code, not an hourly draw.
                </p>
                <p className={classes.referencesNote}>
                    Volta SNAP records the NBC daily hot-water volume, and the NBC 2015 electrical total of 22.4
                    kWh/day, on the base-loads summary. Advanced User Specified stays off, so HOT2000 recalculates
                    electrical use and hot-water draw from the standard occupancy and its own fixture schedule. Those
                    results do not match the recorded code totals. NBC 2020 and NBC 2025 leave the electrical summary at
                    standard operating conditions, because HOT2000 cannot take that table&apos;s hourly profile, and
                    HOT2000 recalculates that summary as well.
                </p>
            </section>

            <section className={classes.section}>
                <h3>Equipment the code table does not list</h3>
                <div className={classes.tableWrapper}>
                    <table className={`${classes.factorTable} ${classes.wrap}`}>
                        <thead>
                            <tr>
                                <th>Case</th>
                                <th>Reference house</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>NBC 2020 heating equipment not in Table 9.36.3.10</td>
                                <td>
                                    Gas-fired warm-air furnace at 92% AFUE. Listed gas furnaces at or below 66 kW still
                                    use 95% AFUE from that table.
                                </td>
                            </tr>
                            <tr>
                                <td>NBC 2025 heating equipment not in Table 9.36.3.10</td>
                                <td>Gas-fired warm-air furnace at 95% AFUE.</td>
                            </tr>
                            <tr>
                                <td>NBC 2015 and NBC 2020 unlisted domestic hot water</td>
                                <td>
                                    Wood, unlisted equipment, and leftover solar become natural gas storage: 279 L, 22
                                    kW, thermal efficiency 0.80. Heat-pump water heaters stay heat pumps at EF 2.1 under
                                    NBC 2020.
                                </td>
                            </tr>
                            <tr>
                                <td>NBC 2025 unlisted domestic hot water</td>
                                <td>
                                    Natural gas storage: 279 L, 30.5 kW, high-draw uniform energy factor from Table
                                    9.36.4.2. Heat-pump water heaters stay heat pumps at UEF 2.23.
                                </td>
                            </tr>
                            <tr>
                                <td>Ontario SB-12 domestic hot water</td>
                                <td>
                                    A blank package energy factor keeps the proposed equipment. A specified energy
                                    factor is imposed, and non-storage equipment becomes same-fuel storage at 184 L.
                                    Drain-water heat recovery is installed at 41.5% recovery, except one-storey
                                    slab-on-grade or unheated-crawl houses. Shower length is 6.5 minutes.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={classes.section}>
                <h3>Known limits</h3>
                <p>
                    Volta SNAP sets every reference house input that HOT2000 exposes. Some code requirements have no
                    matching input, so the closest exposed value is used.
                </p>
                <div className={classes.tableWrapper}>
                    <table className={`${classes.factorTable} ${classes.wrap}`}>
                        <thead>
                            <tr>
                                <th>Topic</th>
                                <th>What to expect</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Hourly gain and hot-water schedules</td>
                                <td>
                                    NBC Tables 9.36.5.4 and 9.36.5.8 are hourly. HOT2000 uses internal, unalterable
                                    schedules.
                                </td>
                            </tr>
                            <tr>
                                <td>Airtightness exponent</td>
                                <td>
                                    NBC 2020 sets a pressure exponent of 0.67. HOT2000&apos;s air-infiltration model
                                    uses its own exponent, commonly 0.68. Volta SNAP can set ACH50 and guarded status.
                                </td>
                            </tr>
                            <tr>
                                <td>Thermostat deadband</td>
                                <td>
                                    The ±0.5 °C deadband in the code is not modelled. Allowable temperature rise stays
                                    at the standard operating condition high setting, 5.5 °C.
                                </td>
                            </tr>
                            <tr>
                                <td>Circulation fans</td>
                                <td>
                                    A heat pump and a furnace share one fan model. HOT2000 does not give the heat pump
                                    its own circulation fan.
                                </td>
                            </tr>
                            <tr>
                                <td>Multi-unit fenestration cap</td>
                                <td>
                                    The 40% multi-unit cap in NBC 9.36.5.14 is not applied. Fenestration stays in the
                                    17–22% band for every building.
                                </td>
                            </tr>
                            <tr>
                                <td>Attached heated garage</td>
                                <td>HOT2000 cannot identify an attached heated garage wall.</td>
                            </tr>
                            <tr>
                                <td>Ontario daily hot water</td>
                                <td>
                                    General mode recalculates draw from the Rule Sets Table 3 fixture schedule and
                                    typically reports about 188 L/day. HOT2000 Ontario Reference House program mode
                                    often reports about 170 L/day for the same house. Both sit in the Rule Sets band of
                                    about 158–197 L/day. On a typical package A house this is about 1.0–1.5 GJ more
                                    domestic hot water in the Volta SNAP run. Equipment efficiency, shower length, and
                                    drain-water heat recovery still follow the package and the Rule Sets.
                                </td>
                            </tr>
                            <tr>
                                <td>Ontario space heating</td>
                                <td>
                                    Space heating can differ from a HOT2000 Ontario Reference House run, typically by
                                    about 1% to 8%, from fenestration heat loss. Ventilation matched on the houses used
                                    to check Ontario SB-12.
                                </td>
                            </tr>
                            <tr>
                                <td>NBC comparison files</td>
                                <td>
                                    NBC 2015, 2020, and 2025 have no (non-EnerGuide) HOT2000 program mode to run as a
                                    comparison. Volta SNAP builds those reference houses from the code articles below.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <ul className={classes.references}>
                    <li>NBC 2015 Division B, 9.36.5, and the 9.36.2 to 9.36.4 tables it references.</li>
                    <li>NBC 2020 Division B, 9.36.5, and the 9.36.2 to 9.36.4 tables it references.</li>
                    <li>NBC 2025 Division B, 9.36.6, and the 9.36.2 to 9.36.4 tables it references.</li>
                    <li>OBC Supplementary Standard SB-12, Chapter 3.</li>
                    <li>
                        EnerGuide Rating System — Rule Sets Reference Document v15.12, for Ontario modelling gaps such
                        as the Table 3 fixture schedule.
                    </li>
                </ul>
            </section>
        </div>
    </div>
);
